import { omit } from 'es-toolkit';

import {
  type Choice,
  type EssaySet,
  type MultipleSet,
  type OxSet,
  type QuizSetData,
  type SubjectiveSet,
} from '@/api';

// context에서 quizList 정보를 받을 때 초기 가공을 담당하는 함수
type AdaptedQuizBase = Omit<
  QuizSetData,
  'oxData' | 'subjectiveData' | 'order' | 'multipleData' | 'type'
> & {
  order: number;
};

type AdaptedMultipleSet = MultipleSet & {
  adaptedChoiceList?: (Choice & { order: number; isAnswer: boolean })[];
};

type AdaptedMultipleQuiz = AdaptedQuizBase & {
  type: 'multiple';
  multipleData: AdaptedMultipleSet;
};

type AdaptedOxQuiz = AdaptedQuizBase & {
  type: 'ox';
  oxData: OxSet;
};

type AdaptedSubjectiveQuiz = AdaptedQuizBase & {
  type: 'subjective';
  subjectiveData: SubjectiveSet;
};

type AdaptedEssayQuiz = AdaptedQuizBase & {
  type: 'essay';
  essayData?: EssaySet;
};

export type AdaptedQuiz =
  | AdaptedMultipleQuiz
  | AdaptedOxQuiz
  | AdaptedSubjectiveQuiz
  | AdaptedEssayQuiz;

const _adaptMultipleQuiz = (
  quiz: QuizSetData,
  index: number,
): AdaptedMultipleQuiz => {
  const { choiceListV2, answerList } = quiz.multipleData as MultipleSet;

  const adaptedMultipleQuiz = {
    ...omit(quiz, ['oxData', 'subjectiveData']),
    type: 'multiple' as const,
    order: index,
    multipleData: {
      ...((quiz.multipleData as MultipleSet) ?? {}),
      adaptedChoiceList: choiceListV2?.map((choice, index) => ({
        ...choice,
        order: index,
        isAnswer: answerList?.includes(String(index)),
      })),
    },
  };

  return adaptedMultipleQuiz;
};

export const quizListDataAdapter = (quizList: QuizSetData[]): AdaptedQuiz[] => {
  return quizList.map((quiz, index) => {
    const type = quiz.type as 'ox' | 'subjective' | 'multiple' | 'essay';
    switch (type) {
      case 'ox':
        return {
          ...omit(quiz, ['multipleData', 'subjectiveData', 'order']),
          type: 'ox',
          order: index,
        } as AdaptedOxQuiz;
      case 'subjective':
        return {
          ...omit(quiz, ['multipleData', 'oxData', 'order']),
          type: 'subjective',
          order: index,
        } as AdaptedSubjectiveQuiz;
      case 'essay':
        return {
          ...omit(quiz, ['multipleData', 'oxData', 'subjectiveData', 'order']),
          type: 'essay',
          order: index,
        } as AdaptedEssayQuiz;
      case 'multiple':
        return _adaptMultipleQuiz(quiz, index);
    }
  });
};
